import { ThemeEvents } from "@theme/events";

const THEME_BTN = "[data-product-media-wishlist]";
const THEME_CARD_BTN = "[data-product-card-wishlist]";
const SWYM_BTN = "#swym-atw-pdp-button";

let variantDocumentBound = false;
let variantSelectedDocumentBound = false;
let swymPdpAnchorMaintainerBound = false;
let swymWishlistRefreshBound = false;
let cardWishlistDelegateBound = false;
let cardWishlistObserverBound = false;
let pdpWishlistDelegateBound = false;
let pdpWishlistSyncWired = false;
let pdpWishlistVisibilityPollId = 0;
let pdpWishlistVisibilityPollCount = 0;

const PDP_WISHLIST_VISIBILITY_POLL_MAX = 120;

function isSwymWishlistReady(swat) {
  if (!swat || typeof swat !== "object") return false;

  const api = /** @type {Record<string, unknown>} */ (swat);
  return (
    typeof api.fetch === "function" ||
    typeof api.getProductDetails === "function" ||
    typeof api.addToWishList === "function" ||
    typeof api.addToWishlist === "function"
  );
}

function getCurrentSwat() {
  return /** @type {Window & { _swat?: object }} */ (window)._swat;
}

/**
 * @param {object} [swat]
 */
function revealPdpWishlistButtons(swat) {
  for (const themeBtn of document.querySelectorAll(THEME_BTN)) {
    if (!(themeBtn instanceof HTMLElement)) continue;

    themeBtn.removeAttribute("data-swym-wishlist-pending");
    themeBtn.removeAttribute("aria-hidden");

    if (swat) {
      syncFromWishlistFetch(themeBtn, swat);
      applyWishlistUiFromSwymButton(themeBtn, document.querySelector(SWYM_BTN));
    }
  }
}

/**
 * @param {object} [swat]
 */
function revealPdpWishlistButtonsWhenFetchWorks(swat) {
  if (!swat || typeof swat !== "object") return false;

  const api = /** @type {{ fetch?: unknown }} */ (swat);
  if (typeof api.fetch !== "function") return false;

  api.fetch(() => revealPdpWishlistButtons(swat));
  return true;
}

/**
 * @param {unknown} raw
 */
function normalizeWishlistProducts(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    const o = /** @type {Record<string, unknown>} */ (raw);
    if (Array.isArray(o.products)) return o.products;
    if (Array.isArray(o.items)) return o.items;
    if (Array.isArray(o.listcontents)) return o.listcontents;
    if (Array.isArray(o.listContents)) return o.listContents;
    if (Array.isArray(o.wishlist)) return o.wishlist;
    if (Array.isArray(o.contents)) return o.contents;
    if (o.list && typeof o.list === "object") {
      const L = /** @type {Record<string, unknown>} */ (o.list);
      if (Array.isArray(L.contents)) return L.contents;
      if (Array.isArray(L.listcontents)) return L.listcontents;
    }
  }
  return [];
}

/**
 * Swym / Shopify sometimes use GIDs; Liquid uses numeric ids — compare a canonical numeric tail.
 * @param {unknown} id
 */
function normalizeSwymEntityId(id) {
  if (id == null) return "";
  const s = String(id).trim();
  const gid = s.match(/\/(?:Product|ProductVariant)\/(\d+)\s*$/i);
  if (gid) return gid[1];
  const tail = s.match(/(\d+)\s*$/);
  return tail ? tail[1] : s;
}

/**
 * @param {unknown} p
 */
function getWishlistLineKeys(p) {
  if (!p || typeof p !== "object") return { empi: null, epi: null };
  const o = /** @type {Record<string, unknown>} */ (p);
  const product =
    o.product && typeof o.product === "object" ? /** @type {Record<string, unknown>} */ (o.product) : null;
  const variant =
    o.variant && typeof o.variant === "object" ? /** @type {Record<string, unknown>} */ (o.variant) : null;
  const empi = o.empi ?? o.Empi ?? o.productId ?? o.product_id ?? o.id ?? product?.id;
  const epi = o.epi ?? o.Epi ?? o.variantId ?? o.variant_id ?? variant?.id;
  return { empi, epi };
}

/**
 * @param {unknown[]} products
 * @param {string | undefined} empi
 * @param {string | undefined} epi
 */
function isVariantInWishlist(products, empi, epi) {
  const list = normalizeWishlistProducts(products);
  if (!list.length || empi == null || epi == null) return false;
  const empiN = normalizeSwymEntityId(empi);
  const epiN = normalizeSwymEntityId(epi);
  if (!epiN) return false;
  return list.some((p) => {
    const { empi: pEmpi, epi: pEpi } = getWishlistLineKeys(p);
    const epiMatch = pEpi != null && normalizeSwymEntityId(pEpi) === epiN;
    if (!epiMatch) return false;
    if (pEmpi == null) return true;
    return normalizeSwymEntityId(pEmpi) === empiN;
  });
}

/**
 * @param {HTMLElement} themeBtn
 * @param {boolean} inWishlist
 */
function setCardWishlistUi(themeBtn, inWishlist) {
  const addLabel = themeBtn.dataset.labelAdd || "";
  const removeLabel = themeBtn.dataset.labelRemove || "";

  themeBtn.setAttribute("aria-pressed", inWishlist ? "true" : "false");
  themeBtn.setAttribute("aria-label", inWishlist && removeLabel ? removeLabel : addLabel);
  themeBtn.classList.toggle("product-card-wishlist__toggle--in-wishlist", inWishlist);
}

/**
 * @param {HTMLElement} themeBtn
 * @param {boolean} inWishlist
 */
function setPdpWishlistUi(themeBtn, inWishlist) {
  const addLabel = themeBtn.dataset.labelAdd || "";
  const removeLabel = themeBtn.dataset.labelRemove || "";

  themeBtn.setAttribute("aria-pressed", inWishlist ? "true" : "false");
  themeBtn.setAttribute("aria-label", inWishlist && removeLabel ? removeLabel : addLabel);
  themeBtn.classList.toggle("product-media-gallery__wishlist-toggle--in-wishlist", inWishlist);
}

/**
 * @param {HTMLElement} themeBtn
 * @param {object} swat
 */
function syncCardFromWishlistFetch(themeBtn, swat) {
  if (typeof swat?.fetch !== "function") return;
  swat.fetch((raw) => {
    const products = normalizeWishlistProducts(raw);
    const empi = themeBtn.dataset.empi;
    const epi = themeBtn.dataset.epi;
    const inList = isVariantInWishlist(products, empi, epi);
    setCardWishlistUi(themeBtn, inList);
  });
}

/**
 * Swym’s `fetch` payload can lag right after add/remove; re-read a few times so the heart state catches up.
 * @param {HTMLElement} themeBtn
 * @param {object} swat
 */
function scheduleCardWishlistResync(themeBtn, swat) {
  const run = () => syncCardFromWishlistFetch(themeBtn, swat);
  run();
  queueMicrotask(run);
  requestAnimationFrame(run);
  setTimeout(run, 80);
  setTimeout(run, 250);
  setTimeout(run, 700);
}

/**
 * @param {object} swat
 */
function scheduleAllCardWishlistResync(swat) {
  const run = () => syncAllCardWishlistButtons(swat);
  run();
  queueMicrotask(run);
  requestAnimationFrame(run);
  setTimeout(run, 80);
  setTimeout(run, 250);
  setTimeout(run, 700);
}

/**
 * @param {HTMLElement} themeBtn
 * @param {object} swat
 */
function schedulePdpWishlistResync(themeBtn, swat) {
  const run = () => syncFromWishlistFetch(themeBtn, swat);
  run();
  queueMicrotask(run);
  requestAnimationFrame(run);
  setTimeout(run, 80);
  setTimeout(run, 250);
  setTimeout(run, 700);
}

/**
 * @param {object} swat
 * @returns {Promise<string | null>}
 */
function ensureListId(swat) {
  const win = /** @type {Window & { swymSelectedListId?: string; swymWishLists?: unknown }} */ (window);
  if (win.swymSelectedListId) return Promise.resolve(win.swymSelectedListId);

  const listsRaw = win.swymWishLists;
  if (Array.isArray(listsRaw) && listsRaw[0] && typeof listsRaw[0] === "object") {
    const first = /** @type {Record<string, unknown>} */ (listsRaw[0]);
    const lid = first.lid != null ? String(first.lid) : null;
    if (lid) {
      win.swymSelectedListId = lid;
      return Promise.resolve(lid);
    }
  }

  if (typeof swat?.fetchLists !== "function") return Promise.resolve(null);

  return new Promise((resolve) => {
    swat.fetchLists({
      callbackFn: (lists) => {
        const arr = Array.isArray(lists) ? lists : [];
        win.swymWishLists = lists;
        const first = arr[0] && typeof arr[0] === "object" ? /** @type {Record<string, unknown>} */ (arr[0]) : null;
        const lid = first?.lid != null ? String(first.lid) : null;
        if (lid) win.swymSelectedListId = lid;
        resolve(lid);
      },
      errorFn: () => resolve(null)
    });
  });
}

/**
 * @param {HTMLElement} themeBtn
 * @returns {{ empi: string | undefined; epi: string | undefined; du: string }}
 */
function getCardProductPayload(themeBtn) {
  const du = themeBtn.dataset.productUrl || themeBtn.getAttribute("data-product-url") || "";
  const empi = themeBtn.dataset.empi ?? themeBtn.getAttribute("data-empi") ?? undefined;
  const epi = themeBtn.dataset.epi ?? themeBtn.getAttribute("data-epi") ?? undefined;
  return { empi, epi, du };
}

/**
 * @param {string} url
 */
function stripUrlQuery(url) {
  if (!url) return "";
  const q = url.indexOf("?");
  return q === -1 ? url : url.slice(0, q);
}

/**
 * Remove Shopify Markets locale prefix (`/en-us/products/...` → `/products/...`).
 * Only when the second segment is `products`, so we do not touch odd paths.
 * @param {string} pathname
 */
function stripMarketsLocalePrefixFromPathname(pathname) {
  if (!pathname || pathname === "/") return pathname;
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length >= 2 && parts[1] === "products" && /^[a-z]{2}(?:-[a-z]{2})?$/i.test(parts[0])) {
    return `/${parts.slice(1).join("/")}`;
  }
  return pathname.startsWith("/") ? pathname : `/${pathname}`;
}

/**
 * Swym wishlist API often returns `sw-unknown-product` when `du` includes a Markets locale segment;
 * canonicalize to `{origin}/products/{handle}` (no `/en-us/` etc.).
 * @param {string} url
 */
function swymCanonicalProductPageUrl(url) {
  if (!url) return "";
  try {
    const u = new URL(url, window.location.origin);
    const path = stripMarketsLocalePrefixFromPathname(u.pathname);
    return stripUrlQuery(`${u.origin}${path}`);
  } catch {
    return stripUrlQuery(url);
  }
}

/**
 * Swym collection docs: use `/products/{handle}` for getProductDetails (not Markets-prefixed paths).
 * @param {string} productUrl
 */
function swymProductPathForDetails(productUrl) {
  try {
    const pathname = new URL(productUrl, window.location.origin).pathname;
    const m = pathname.match(/\/products\/([^/?#]+)/);
    if (m) return `/products/${m[1]}`;
    return pathname.split("?")[0] || "";
  } catch {
    return "";
  }
}

/**
 * Swym’s collection example uses the URL as the shopper sees it (link `href` + `window.location.origin`), not
 * `shop.url`, so `du` matches the indexed storefront host.
 * @param {HTMLElement} themeBtn
 */
function storefrontProductPageDu(themeBtn) {
  const raw = themeBtn.dataset.productUrl || themeBtn.getAttribute("data-product-url") || "";
  const path = swymProductPathForDetails(raw);
  if (!path || typeof window === "undefined" || !window.location?.origin) return "";
  return stripUrlQuery(`${window.location.origin}${path}`);
}

/**
 * @param {unknown} raw
 */
function asProductJsonRecord(raw) {
  if (!raw || typeof raw !== "object") return {};
  return /** @type {Record<string, unknown>} */ (raw);
}

/**
 * Prefer the theme control’s `data-epi` when Swym’s variant list contains it (PDP size/color from the picker).
 * @param {HTMLElement} themeBtn
 * @param {unknown[]} variants
 * @param {Record<string, unknown> | null} firstVariant
 * @param {Record<string, unknown>} pj
 */
function resolveWishlistEpiPreferSelected(themeBtn, variants, firstVariant, pj) {
  const themeEpi = themeBtn.dataset.epi;
  if (themeEpi != null && variants.length > 0) {
    const target = normalizeSwymEntityId(themeEpi);
    for (const v of variants) {
      if (!v || typeof v !== "object") continue;
      const id = /** @type {Record<string, unknown>} */ (v).id;
      if (id != null && normalizeSwymEntityId(id) === target) return id;
    }
  }
  return themeEpi ?? pj.epi ?? firstVariant?.id;
}

/**
 * PDP: `variant:update` runs after section HTML is fetched; `variant:selected` + URL replace are async.
 * Read the live line item variant from the cart form or `?variant=` so the wishlist matches the chosen size.
 * @param {HTMLElement} themeBtn
 */
function getLiveVariantIdForPdpWishlist(themeBtn) {
  if (!themeBtn.matches(THEME_BTN)) return undefined;
  const scope =
    themeBtn.closest("product-information, featured-product-information") ??
    themeBtn.closest(".shopify-section") ??
    document;

  const formInput =
    scope.querySelector("product-form-component input[name='id']") ??
    scope.querySelector("form[action*='/cart/add'] input[name='id']") ??
    document.querySelector("product-form-component input[name='id']");

  if (formInput instanceof HTMLInputElement && formInput.value?.trim()) {
    return formInput.value.trim();
  }

  try {
    const fromUrl = new URL(window.location.href).searchParams.get("variant");
    if (fromUrl?.trim()) return fromUrl.trim();
  } catch {
    /* invalid URL */
  }

  const fromBtn = themeBtn.dataset.epi;
  if (fromBtn != null && String(fromBtn).trim() !== "") return String(fromBtn).trim();

  return undefined;
}

/**
 * Wishlist Plus expects `#swym-atw-pdp-button` in the DOM; `media-gallery` `replaceWith` on variant change can
 * remove Swym’s node and their callbacks then hit `null.classList`. Recreate a minimal anchor when missing.
 */
function ensureSwymPdpWishlistAnchor() {
  if (typeof document === "undefined") return;
  if (document.querySelector(SWYM_BTN)) return;
  const host = document.querySelector(THEME_BTN);
  if (!(host instanceof HTMLElement)) return;

  const btn = document.createElement("button");
  btn.type = "button";
  btn.id = "swym-atw-pdp-button";
  btn.setAttribute("aria-hidden", "true");
  btn.tabIndex = -1;
  btn.className = "visually-hidden";
  btn.style.cssText =
    "position:fixed;inset-block-start:0;inset-inline-start:0;width:1px;height:1px;margin:0;padding:0;overflow:hidden;clip-path:inset(50%);border:0;white-space:nowrap;";
  const empi = host.dataset.empi;
  const epi = host.dataset.epi;
  if (empi) btn.dataset.empi = String(empi);
  if (epi) btn.dataset.epi = String(epi);
  document.body.append(btn);
}

function bindSwymPdpAnchorMaintainer() {
  if (swymPdpAnchorMaintainerBound) return;
  swymPdpAnchorMaintainerBound = true;
  document.addEventListener(ThemeEvents.sectionMorphed, () => {
    queueMicrotask(() => ensureSwymPdpWishlistAnchor());
  });
}

/**
 * Swym rejects `sw-unknown-product` when `epi` is not the variant their catalog expects for `du`.
 * On **cards**, prefer the SDK default when there are multiple variants (collection pattern; avoids bad pairings).
 * On **PDP**, the shopper picked a size — always prefer `data-epi` when it appears in `getProductDetails().variants`.
 * @param {HTMLElement} themeBtn
 * @param {Record<string, unknown>} pj
 * @param {unknown[]} variants
 * @param {Record<string, unknown> | null} firstVariant
 */
function resolveWishlistEpi(themeBtn, pj, variants, firstVariant) {
  const isPdp = themeBtn.matches(THEME_BTN);
  if (isPdp) {
    return resolveWishlistEpiPreferSelected(themeBtn, variants, firstVariant, pj);
  }

  const themeEpi = themeBtn.dataset.epi;
  if (variants.length > 1) {
    return pj.epi ?? firstVariant?.id ?? themeEpi;
  }
  if (themeEpi != null && variants.length > 0) {
    const target = normalizeSwymEntityId(themeEpi);
    for (const v of variants) {
      if (!v || typeof v !== "object") continue;
      const id = /** @type {Record<string, unknown>} */ (v).id;
      if (id != null && normalizeSwymEntityId(id) === target) return id;
    }
  }
  return pj.epi ?? firstVariant?.id ?? themeEpi;
}

/**
 * Merge Swym getProductDetails() output with the card’s URL — same shape as Swym’s collection example.
 * @see https://developers.getswym.com/docs/collection-page-buttons
 * @param {HTMLElement} themeBtn
 * @param {Record<string, unknown>} productJson
 */
function buildSwymWishlistProductFromDetails(themeBtn, productJson) {
  const rawDu = themeBtn.dataset.productUrl || themeBtn.getAttribute("data-product-url") || "";
  const duFallback = stripUrlQuery(rawDu);
  const pj = productJson;
  const variants = Array.isArray(pj.variants) ? pj.variants : [];
  const firstVariant =
    variants[0] && typeof variants[0] === "object" ? /** @type {Record<string, unknown>} */ (variants[0]) : null;

  const pjHasProduct =
    pj &&
    (pj.empi != null ||
      pj.id != null ||
      pj.epi != null ||
      (Array.isArray(pj.variants) && pj.variants.length > 0) ||
      (typeof pj.dt === "string" && pj.dt.length > 0) ||
      (typeof pj.title === "string" && pj.title.length > 0));

  const empi = pj.empi ?? pj.id ?? themeBtn.dataset.empi;
  const epi = resolveWishlistEpi(themeBtn, pj, variants, firstVariant);
  const dt = String(pj.dt ?? pj.title ?? "");
  const iu = String(pj.iu ?? "");
  const prRaw = pj.pr;
  const pr = typeof prRaw === "number" ? prRaw : Number(prRaw) || 0;

  const pjDuRaw = pjHasProduct && pj.du != null && String(pj.du).trim() !== "" ? stripUrlQuery(String(pj.du)) : "";
  const duFromSdk = pjDuRaw ? swymCanonicalProductPageUrl(pjDuRaw) : "";
  const duFromBrowser = storefrontProductPageDu(themeBtn);
  // When the SDK returned product data, prefer its canonical `du` (Swym index); else storefront URL from the card.
  const du =
    pjHasProduct && duFromSdk ? duFromSdk : duFromBrowser || duFromSdk || swymCanonicalProductPageUrl(duFallback);

  return {
    empi,
    epi,
    du,
    dt,
    iu,
    pr
  };
}

/**
 * @param {object} swat
 * @param {{ empi?: unknown; epi?: unknown; du: string; dt?: string; iu?: string; pr?: number }} product
 * @param {() => void} onSuccess
 * @param {(err?: unknown) => void} onError
 */
function addProductToWishlist(swat, product, onSuccess, onError) {
  const addFn = swat.addToWishList ?? swat.addToWishlist;
  if (typeof addFn === "function") {
    addFn.call(swat, product, onSuccess, onError);
    return;
  }

  void ensureListId(swat).then((lid) => {
    if (!lid || typeof swat.addToList !== "function") {
      onError();
      return;
    }
    swat.addToList(lid, product, onSuccess, onError);
  });
}

/**
 * @param {object} swat
 * @param {{ empi?: unknown; epi?: unknown; du: string; dt?: string; iu?: string; pr?: number }} product
 * @param {() => void} onSuccess
 * @param {(err?: unknown) => void} onError
 */
function removeProductFromWishlist(swat, product, onSuccess, onError) {
  const removeFn = swat.removeFromWishList ?? swat.removeFromWishlist;
  if (typeof removeFn === "function") {
    removeFn.call(swat, product, onSuccess, onError);
    return;
  }

  void ensureListId(swat).then((lid) => {
    if (!lid || typeof swat.deleteFromList !== "function") {
      onError();
      return;
    }
    swat.deleteFromList(lid, product, onSuccess, onError);
  });
}

/**
 * Shared Swym wishlist toggle (cards + PDP): `getProductDetails` + add/remove list APIs.
 * @param {HTMLElement} themeBtn
 * @param {object} swat
 * @param {(el: HTMLElement, on: boolean) => void} setWishlistUi
 * @param {(el: HTMLElement, s: object) => void} scheduleResync
 */
function toggleSwymWishlistFromProductDetails(themeBtn, swat, setWishlistUi, scheduleResync) {
  if (themeBtn.matches(THEME_BTN)) {
    const live = getLiveVariantIdForPdpWishlist(themeBtn);
    if (live != null) themeBtn.dataset.epi = live;
  }

  const productUrlRaw = themeBtn.dataset.productUrl || themeBtn.getAttribute("data-product-url") || "";
  const fallback = getCardProductPayload(themeBtn);
  if (!fallback.du || fallback.empi == null || fallback.epi == null) return;

  const pressed = themeBtn.getAttribute("aria-pressed") === "true";
  const onError = () => {
    scheduleResync(themeBtn, swat);
  };

  /**
   * @param {Record<string, unknown>} productJson
   */
  const runWithDetails = (productJson) => {
    const p = buildSwymWishlistProductFromDetails(themeBtn, productJson);
    if (!p.du || p.empi == null || p.epi == null) return;

    const themeEpi = themeBtn.dataset.epi;
    const onSuccess = () => {
      if (!pressed && themeEpi != null && normalizeSwymEntityId(p.epi) !== normalizeSwymEntityId(themeEpi)) {
        scheduleResync(themeBtn, swat);
        return;
      }
      setWishlistUi(themeBtn, !pressed);
      scheduleResync(themeBtn, swat);
    };

    if (pressed) {
      removeProductFromWishlist(swat, p, onSuccess, onError);
    } else {
      addProductToWishlist(swat, p, onSuccess, onError);
    }
  };

  const detailsPath = swymProductPathForDetails(productUrlRaw);
  const gpd = swat.getProductDetails;
  if (typeof gpd === "function" && detailsPath) {
    gpd.call(
      swat,
      { du: detailsPath },
      /** @param {unknown} productJson */ (productJson) => {
        runWithDetails(asProductJsonRecord(productJson));
      },
      () => {
        runWithDetails({});
      }
    );
    return;
  }

  runWithDetails({});
}

/**
 * @param {HTMLElement} themeBtn
 * @param {object} swat
 */
function toggleCardWishlist(themeBtn, swat) {
  toggleSwymWishlistFromProductDetails(themeBtn, swat, setCardWishlistUi, scheduleCardWishlistResync);
}

/**
 * @param {HTMLElement} themeBtn
 * @param {object} swat
 */
function togglePdpWishlist(themeBtn, swat) {
  toggleSwymWishlistFromProductDetails(themeBtn, swat, setPdpWishlistUi, schedulePdpWishlistResync);
}

/**
 * Sliders clone slides with `cloneNode(true)`; per-node listeners are not copied, and a copied
 * `data-swym-card-wishlist-bound` would block binding. Use capture delegation + sync for new nodes.
 * Binds at module load — do not wait for `SwymCallbacks` or `window._swat` (those can be late).
 */
function bindCardWishlistDelegation() {
  if (cardWishlistDelegateBound) return;
  cardWishlistDelegateBound = true;

  document.addEventListener(
    "click",
    (event) => {
      const themeBtn = event.target instanceof Element ? event.target.closest(THEME_CARD_BTN) : null;
      if (!(themeBtn instanceof HTMLElement)) return;

      const swat = /** @type {Window & { _swat?: { fetch?: unknown } }} */ (window)._swat;
      if (!swat || typeof swat.fetch !== "function") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      toggleCardWishlist(themeBtn, swat);
    },
    true
  );
}

/**
 * @param {object} swat
 */
function syncAllCardWishlistButtons(swat) {
  for (const themeBtn of document.querySelectorAll(THEME_CARD_BTN)) {
    if (!(themeBtn instanceof HTMLElement)) continue;
    syncCardFromWishlistFetch(themeBtn, swat);
  }
}

/**
 * @param {object} swat
 */
function observeCardWishlistButtons(swat) {
  if (cardWishlistObserverBound) return;
  cardWishlistObserverBound = true;

  const syncNew = () => {
    for (const themeBtn of document.querySelectorAll(THEME_CARD_BTN)) {
      if (!(themeBtn instanceof HTMLElement)) continue;
      if (themeBtn.dataset.swymCardWishlistSynced === "1") continue;
      themeBtn.dataset.swymCardWishlistSynced = "1";
      syncCardFromWishlistFetch(themeBtn, swat);
    }
  };

  syncNew();
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const n of m.addedNodes) {
        if (!(n instanceof Element)) continue;
        if (n.matches(THEME_CARD_BTN) || n.querySelector(THEME_CARD_BTN)) {
          syncNew();
          return;
        }
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
}

/**
 * Swym toggles `atw-button-added` on #swym-atw-pdp-button — use that as the source of truth for the
 * themed control (swat.fetch payload shape varies by app version).
 * @param {HTMLElement} themeBtn
 * @param {Element | null} swymBtn
 */
function applyWishlistUiFromSwymButton(themeBtn, swymBtn) {
  if (!(swymBtn instanceof HTMLElement)) return;
  const added = swymBtn.classList.contains("atw-button-added");
  const addLabel = themeBtn.dataset.labelAdd || "";
  const removeLabel = themeBtn.dataset.labelRemove || "";

  themeBtn.setAttribute("aria-pressed", added ? "true" : "false");
  themeBtn.setAttribute("aria-label", added && removeLabel ? removeLabel : addLabel);
  themeBtn.classList.toggle("product-media-gallery__wishlist-toggle--in-wishlist", added);
}

/**
 * @param {HTMLElement} themeBtn
 * @param {object} swat
 */
function syncFromWishlistFetch(themeBtn, swat) {
  if (typeof swat?.fetch !== "function") return;
  swat.fetch((raw) => {
    const products = normalizeWishlistProducts(raw);
    const empi = themeBtn.dataset.empi;
    const epi = themeBtn.dataset.epi;
    const inList = isVariantInWishlist(products, empi, epi);
    const swymBtn = document.querySelector(SWYM_BTN);
    if (inList) {
      const addLabel = themeBtn.dataset.labelAdd || "";
      const removeLabel = themeBtn.dataset.labelRemove || "";
      themeBtn.setAttribute("aria-pressed", "true");
      themeBtn.setAttribute("aria-label", removeLabel || addLabel);
      themeBtn.classList.add("product-media-gallery__wishlist-toggle--in-wishlist");
      return;
    }
    applyWishlistUiFromSwymButton(themeBtn, swymBtn);
  });
}

/**
 * @param {object} [swat]
 */
function updatePdpWishlistVisibility(swat) {
  const activeSwat = swat ?? getCurrentSwat();
  const canShow = isSwymWishlistReady(activeSwat);

  if (revealPdpWishlistButtonsWhenFetchWorks(activeSwat)) return;

  for (const themeBtn of document.querySelectorAll(THEME_BTN)) {
    if (!(themeBtn instanceof HTMLElement)) continue;

    themeBtn.toggleAttribute("data-swym-wishlist-pending", !canShow);
    themeBtn.toggleAttribute("aria-hidden", !canShow);

    if (canShow && activeSwat) {
      syncFromWishlistFetch(themeBtn, activeSwat);
      applyWishlistUiFromSwymButton(themeBtn, document.querySelector(SWYM_BTN));
    }
  }
}

/**
 * @param {object} [swat]
 */
function startPdpWishlistVisibilityPolling(swat) {
  updatePdpWishlistVisibility(swat);
  if (pdpWishlistVisibilityPollId) return;

  pdpWishlistVisibilityPollId = window.setInterval(() => {
    pdpWishlistVisibilityPollCount += 1;
    updatePdpWishlistVisibility(swat ?? getCurrentSwat());

    if (pdpWishlistVisibilityPollCount >= PDP_WISHLIST_VISIBILITY_POLL_MAX) {
      clearInterval(pdpWishlistVisibilityPollId);
      pdpWishlistVisibilityPollId = 0;
    }
  }, 1000);
}

/**
 * @param {(el: HTMLElement) => void} callback
 */
function whenSwymPdpButtonReady(callback) {
  const tryNow = document.querySelector(SWYM_BTN);
  if (tryNow instanceof HTMLElement) {
    callback(tryNow);
    return;
  }

  const mo = new MutationObserver(() => {
    const el = document.querySelector(SWYM_BTN);
    if (el instanceof HTMLElement) {
      mo.disconnect();
      callback(el);
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
}

/**
 * @param {HTMLElement} themeBtn
 * @param {object} swat
 */
function handlePdpWishlistClick(themeBtn, swat) {
  togglePdpWishlist(themeBtn, swat);
}

/**
 * One-time wiring: Swym’s hidden PDP button drives state; `[data-product-media-wishlist]` may mount after `_swat`
 * (deferred module, section morph), so clicks use document delegation — same idea as card wishlist.
 * @param {object} swat
 */
function wirePdpWishlistUi(swat) {
  if (pdpWishlistSyncWired) return;
  pdpWishlistSyncWired = true;

  const refreshAllPdp = () => {
    const swymBtn = document.querySelector(SWYM_BTN);
    for (const themeBtn of document.querySelectorAll(THEME_BTN)) {
      if (!(themeBtn instanceof HTMLElement)) continue;
      applyWishlistUiFromSwymButton(themeBtn, swymBtn);
      syncFromWishlistFetch(themeBtn, swat);
    }
    updatePdpWishlistVisibility(swat);
  };

  whenSwymPdpButtonReady((swymBtn) => {
    refreshAllPdp();
    const classMo = new MutationObserver(refreshAllPdp);
    classMo.observe(swymBtn, { attributes: true, attributeFilter: ["class"] });
  });

  const domMo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const n of m.addedNodes) {
        if (!(n instanceof Element)) continue;
        if (n.matches(THEME_BTN) || n.querySelector(THEME_BTN)) {
          ensureSwymPdpWishlistAnchor();
          updatePdpWishlistVisibility(swat);
          refreshAllPdp();
          return;
        }
      }
    }
  });
  domMo.observe(document.documentElement, { childList: true, subtree: true });
}

/**
 * PDP wishlist: capture delegation so buttons work even when they appear after Swym init.
 */
function bindPdpWishlistDelegation() {
  if (pdpWishlistDelegateBound) return;
  pdpWishlistDelegateBound = true;

  document.addEventListener(
    "click",
    (event) => {
      const themeBtn = event.target instanceof Element ? event.target.closest(THEME_BTN) : null;
      if (!(themeBtn instanceof HTMLElement)) return;

      const swat = /** @type {Window & { _swat?: { fetch?: unknown } }} */ (window)._swat;
      if (!swat || typeof swat.fetch !== "function") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      ensureSwymPdpWishlistAnchor();
      handlePdpWishlistClick(themeBtn, swat);
    },
    true
  );
}

/**
 * @param {HTMLElement} themeBtn
 * @param {Event} event
 */
function onVariantUpdate(themeBtn, event) {
  const detail = /** @type {{ resource?: { id?: string | number }; data?: { productId?: string | number } }} */ (
    /** @type {CustomEvent} */ (event).detail
  );
  const variant = detail?.resource;
  const productId = detail?.data?.productId;
  if (!variant?.id) return;
  if (productId && String(themeBtn.dataset.empi) !== String(productId)) return;

  themeBtn.dataset.epi = String(variant.id);
  const swymBtn = document.querySelector(SWYM_BTN);
  if (swymBtn instanceof HTMLElement) {
    swymBtn.dataset.epi = String(variant.id);
  }

  const swat = window._swat;
  ensureSwymPdpWishlistAnchor();
  revealPdpWishlistButtonsWhenFetchWorks(/** @type {object} */ (swat));
  schedulePdpWishlistResync(themeBtn, /** @type {object} */ (swat));
  applyWishlistUiFromSwymButton(themeBtn, document.querySelector(SWYM_BTN));
}

function bindAll(swat) {
  wirePdpWishlistUi(swat);
}

/**
 * Swym runs queued callbacks when the SDK becomes ready. This file is often a `type="module"`
 * script that loads *after* Swym has already flushed `SwymCallbacks`, so we must also run
 * when `window._swat` is already present (and poll briefly for a late `_swat` assignment).
 * @param {object} swat
 */
function initSwymWishlistToggles(swat) {
  if (!swat || typeof swat.fetch !== "function") return;

  revealPdpWishlistButtonsWhenFetchWorks(swat);
  ensureSwymPdpWishlistAnchor();
  bindSwymPdpAnchorMaintainer();
  startPdpWishlistVisibilityPolling(swat);
  bindAll(swat);
  observeCardWishlistButtons(swat);

  if (!swat.evtLayer || swymWishlistRefreshBound) return;
  swymWishlistRefreshBound = true;

  const refreshAll = () => {
    ensureSwymPdpWishlistAnchor();
    updatePdpWishlistVisibility(swat);
    for (const themeBtn of document.querySelectorAll(THEME_BTN)) {
      if (!(themeBtn instanceof HTMLElement)) continue;
      syncFromWishlistFetch(themeBtn, swat);
      applyWishlistUiFromSwymButton(themeBtn, document.querySelector(SWYM_BTN));
    }
    scheduleAllCardWishlistResync(swat);
  };

  swat.evtLayer.addEventListener("sw:addedtowishlist", refreshAll);
  swat.evtLayer.addEventListener("sw:removedfromwishlist", refreshAll);
}

if (!variantSelectedDocumentBound) {
  variantSelectedDocumentBound = true;
  document.addEventListener(ThemeEvents.variantSelected, (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const detail = /** @type {{ resource?: { id?: string } } | undefined} */ (
      /** @type {CustomEvent} */ (event).detail
    );
    const id = detail?.resource?.id;
    if (!id) return;
    const section = target.closest("product-information, featured-product-information");
    if (!section) return;
    for (const themeBtn of section.querySelectorAll(THEME_BTN)) {
      if (!(themeBtn instanceof HTMLElement)) continue;
      themeBtn.dataset.epi = String(id);
    }
    ensureSwymPdpWishlistAnchor();
    revealPdpWishlistButtonsWhenFetchWorks(getCurrentSwat());
    const swymBtn = document.querySelector(SWYM_BTN);
    if (swymBtn instanceof HTMLElement) swymBtn.dataset.epi = String(id);
  });
}

if (!variantDocumentBound) {
  variantDocumentBound = true;
  document.addEventListener(ThemeEvents.variantUpdate, (event) => {
    for (const themeBtn of document.querySelectorAll(THEME_BTN)) {
      if (!(themeBtn instanceof HTMLElement)) continue;
      onVariantUpdate(themeBtn, event);
    }
  });
}

bindCardWishlistDelegation();
bindPdpWishlistDelegation();
startPdpWishlistVisibilityPolling();

window.SwymCallbacks = window.SwymCallbacks || [];
window.SwymCallbacks.push(initSwymWishlistToggles);

const swatAtLoad = getCurrentSwat();
if (swatAtLoad && typeof (/** @type {{ fetch?: unknown }} */ (swatAtLoad).fetch) === "function") {
  initSwymWishlistToggles(swatAtLoad);
}

let swatPollCount = 0;
const swatPollMax = 120;
const swatPollId = setInterval(() => {
  swatPollCount += 1;
  const swat = getCurrentSwat();
  updatePdpWishlistVisibility(swat);
  if (swat && typeof (/** @type {{ fetch?: unknown }} */ (swat).fetch) === "function") {
    revealPdpWishlistButtonsWhenFetchWorks(swat);
    initSwymWishlistToggles(swat);
    clearInterval(swatPollId);
    return;
  }
  if (swatPollCount >= swatPollMax) clearInterval(swatPollId);
}, 100);
